import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api-client";
import { saveEvaluationDraftNow } from "@/lib/draft-producer-runner";
import { cn } from "@/lib/utils";
import type { AdlItem } from "@/hooks/queries/adl-item-types";
import {
  clinicianAdlItems,
  isInvalidScore,
  isValidScore,
  scoresForTask,
  useAdlItems,
  MOTOR_ITEM_COUNT,
  SCORE_COLUMNS,
} from "@/hooks/queries/useAdlItems";
import { useAmpsTasks } from "@/hooks/queries/useAmpsTasks";
import { EVAL_ERROR } from "@/hooks/queries/evaluation-draft-types";
import { reconcileEvaluationDetailCache } from "@/hooks/queries/useGetEvaluation";
import {
  isVersionConflict,
  versionConflictServerState,
} from "@/hooks/queries/useSaveEvaluationDraft";
import { toEvaluationDraftBody } from "@/routes/evaluation/evaluation-draft-body";
import { useEvaluationDraft } from "@/routes/evaluation/evaluation-draft-store";
import {
  activeTaskBanner,
  computeScoringCompleteness,
  CONTINUE_TOOLTIP_BLOCKED,
  CONTINUE_TOOLTIP_ENABLED,
} from "@/routes/evaluation/scoring-validation";

// Story 6.5 — the Task-Tabbed Scoring Workspace (Step 3, Screen 06). Ported from the
// throwaway `evaluation-mockup/ScoringStep.tsx`, but reading/writing the REAL draft
// store (Story 6.7) and the REAL 35-item dictionary (`useAdlItems` → `clinicianAdlItems`)
// instead of the mockup's `EvalTask`/`onTasksChange` props + static `SKILL_ITEMS`.
//
// Scope: Story 6.5 delivered the scoring ENTRY experience — tabs, the 35-item × 4-column
// grid, 1–4 hard input validation, the unscored-vs-invalid distinction, keyboard nav +
// a11y, and per-task save-on-blur. Story 6.6 adds the 34/35 advisory layer on top: the
// per-task banner + the Continue-to-Review gate here, and the topbar aggregate pill via
// ResumeEvaluationRoute (computed from the shared scoring-validation helper). The submit
// endpoint exists (6.6 backend) but has no FE caller until Story 9.5; results are later.

// =========================================================================== //
// Presentational core — a single task's 35-item × 4-column entry grid.
//
// Takes (items, scores, onScoreChange) and is FREE of the eval-draft store, so the
// calibration scoring view (Story 8.x / FR41) can reuse it without the draft coupling.
// Tabs + persistence live in the eval container below.
// =========================================================================== //

interface ScoreEntryGridBaseProps {
  /** The resolved clinician scale (35 items) — index i ↔ score index i. */
  items: AdlItem[];
  /** Positional scores for the active task: 1–4 or `null` (unscored), length === items.length. */
  scores: (number | null)[];
  /** A cell lost focus — the eval container persists on blur (optional; calibration omits). */
  onCellBlur?: () => void;
  /** Cmd/Ctrl + Left/Right tab switch (eval container only; calibration has no tabs). */
  onSwitchTab?: (direction: 1 | -1) => void;
  /** 1-based task position, for cell accessible labels ("Task N item M …"). */
  taskPosition: number;
  /** Accessible label for the grid region. */
  gridLabel: string;
  /**
   * A cell to focus when set (Story 6.8 — the Validation Banner's correction link lands
   * the clinician on the offending item). null/undefined leaves focus alone.
   */
  focusItemIndex?: number | null;
  /**
   * Called once right after `focusItemIndex` is focused, so the parent can clear the
   * one-shot request — otherwise focus would be re-yanked on a later manual return to
   * this task (the grid remounts per task), and a repeat jump to the same cell wouldn't
   * re-fire (the index dep wouldn't change). Clearing → re-arming makes both correct.
   */
  onFocusHandled?: () => void;
}

// The edit callback is required EXCEPT in the read-only variant (Story 8.8,
// Screen 18 calibration case log): inputs are `readOnly` and untabbable, the grid
// is inert (`pointer-events-none`), cells take the muted treatment (no focus
// ring), and no callback ever fires — so a read-only host may omit it entirely,
// while an editable host (the 6.5 eval container) still can't forget it.
type ScoreEntryGridProps = ScoreEntryGridBaseProps &
  (
    | {
        readOnly: true;
        /** Never fired in the read-only variant — omit it. */
        onScoreChange?: (index: number, value: number | null) => void;
      }
    | {
        /** Default `false` — the 6.5 eval host is unchanged. */
        readOnly?: false;
        /** Commit a cell edit (number 1–4, or `null` to clear). The store is updated synchronously. */
        onScoreChange: (index: number, value: number | null) => void;
      }
  );

/**
 * The score-entry grid for ONE task.
 *
 * a11y (AC 6): a real `role="grid"` (NOT a bare `role="group"`) with `aria-rowcount` /
 * `aria-colcount`, `role="row"` / `role="gridcell"`, and a per-cell accessible label.
 * The grid is modeled COLUMN-primary: each visual column is a `role="row"` so keyboard
 * + Tab traversal runs column-by-column (item 1→35 down each column, AC 5) — matching
 * the AMPS paper form and Screen 06. `aria-rowcount` is therefore the column count and
 * `aria-colcount` the items-per-column; the per-cell labels carry the true item meaning.
 */
export function ScoreEntryGrid({
  items,
  scores,
  onScoreChange,
  onCellBlur,
  onSwitchTab,
  taskPosition,
  gridLabel,
  focusItemIndex,
  onFocusHandled,
  readOnly = false,
}: ScoreEntryGridProps) {
  const idPrefix = React.useId();
  const [liveMessage, setLiveMessage] = React.useState("");
  const inputsRef = React.useRef<(HTMLInputElement | null)[]>([]);

  const itemCount = items.length;
  const perColumn = Math.ceil(itemCount / SCORE_COLUMNS);

  // The column layout (9/9/9/8 for 35 items). Rebuilt only when the item set changes.
  const columns = React.useMemo(
    () =>
      Array.from({ length: SCORE_COLUMNS }, (_, col) =>
        items
          .slice(col * perColumn, (col + 1) * perColumn)
          .map((item, rowIdx) => ({ item, flatIdx: col * perColumn + rowIdx })),
      ),
    [items, perColumn],
  );

  // Callbacks are kept STABLE (refs hold the latest props) so the memoized `ScoreCell`s
  // below skip re-rendering on a keystroke: only the edited cell's `value` changes, so
  // only it re-renders — never the whole 35-cell grid (AC 12, "commit is per-cell").
  const onScoreChangeRef = React.useRef(onScoreChange);
  onScoreChangeRef.current = onScoreChange;
  const onCellBlurRef = React.useRef(onCellBlur);
  onCellBlurRef.current = onCellBlur;
  const onSwitchTabRef = React.useRef(onSwitchTab);
  onSwitchTabRef.current = onSwitchTab;

  const registerInput = React.useCallback(
    (flatIdx: number, el: HTMLInputElement | null) => {
      inputsRef.current[flatIdx] = el;
    },
    [],
  );

  const focusCell = React.useCallback((flatIdx: number) => {
    const target = inputsRef.current[flatIdx];
    if (target) {
      target.focus();
      target.select();
    }
  }, []);

  // Story 6.8 — land focus on the cell a correction link pointed at, then tell the parent
  // to clear the one-shot request (the grid is keyed by task, so it remounts on a tab
  // switch; without the clear, a later manual return would re-yank focus here).
  // The itemIndex is server-supplied (submit 422 range blockers); an index past the grid
  // is clamped to the last cell so focus lands *somewhere* the OT can see, instead of a
  // silent no-op that leaves the correction link feeling dead (6.8 review, leg 1).
  // An EMPTY grid (itemCount 0, e.g. the dictionary not yet loaded) keeps the request
  // pending instead of clearing it against Math.min(…, -1) — the effect re-runs when
  // itemCount changes, so focus lands once the cells exist (PR #581 review, issue 7).
  React.useEffect(() => {
    if (itemCount > 0 && focusItemIndex != null && focusItemIndex >= 0) {
      focusCell(Math.min(focusItemIndex, itemCount - 1));
      onFocusHandled?.();
    }
  }, [focusItemIndex, focusCell, onFocusHandled, itemCount]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, flatIdx: number) => {
      const onSwitch = onSwitchTabRef.current;
      // Cmd/Ctrl + Left/Right switches between task tabs (AC 5).
      if (
        onSwitch &&
        (event.metaKey || event.ctrlKey) &&
        (event.key === "ArrowLeft" || event.key === "ArrowRight")
      ) {
        event.preventDefault();
        onSwitch(event.key === "ArrowRight" ? 1 : -1);
        return;
      }

      // Arrow-key navigation between cells (AC 5). Down/Enter → next item (down the
      // column), Up → previous, Left/Right → previous/next column.
      if (event.key === "ArrowDown" || event.key === "Enter") {
        event.preventDefault();
        focusCell(Math.min(flatIdx + 1, itemCount - 1));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        focusCell(Math.max(flatIdx - 1, 0));
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        if (flatIdx + perColumn < itemCount) focusCell(flatIdx + perColumn);
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        if (flatIdx - perColumn >= 0) focusCell(flatIdx - perColumn);
        return;
      }

      // Reject any printable character that is not 1–4, at the input layer (AC 3).
      if (
        event.key.length === 1 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !isValidScore(event.key)
      ) {
        event.preventDefault();
        setLiveMessage("Scores must be a whole number from 1 to 4.");
      }
    },
    [focusCell, itemCount, perColumn],
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, flatIdx: number) => {
      const raw = event.target.value;
      if (raw === "") {
        onScoreChangeRef.current?.(flatIdx, null);
        setLiveMessage("");
        return;
      }
      // The last character wins (overtype of a 1-char field). A non-1–4 char is a safety
      // net for paste / IME — NOT committed, so the cell reverts (AC 3).
      const lastChar = raw.slice(-1);
      if (isValidScore(lastChar)) {
        onScoreChangeRef.current?.(flatIdx, Number(lastChar));
        setLiveMessage("");
      } else {
        setLiveMessage("Scores must be a whole number from 1 to 4.");
      }
    },
    [],
  );

  const handleBlur = React.useCallback(() => {
    onCellBlurRef.current?.();
  }, []);

  return (
    <div className="grid gap-2">
      <div
        role="grid"
        aria-label={gridLabel}
        aria-rowcount={SCORE_COLUMNS}
        aria-colcount={perColumn}
        className={cn(
          "grid grid-cols-2 gap-x-[18px] gap-y-1 xl:grid-cols-4",
          // Read-only (Story 8.8): the whole grid is inert — no pointer target,
          // no hover affordance. The "Read only" pill above signals the state.
          readOnly && "pointer-events-none",
        )}
      >
        {columns.map((columnItems, colIdx) => (
          <div
            role="row"
            key={colIdx}
            className={cn(
              colIdx < SCORE_COLUMNS - 1 &&
                "xl:border-r xl:border-[color:var(--border-secondary)] xl:pr-[18px]",
            )}
          >
            {columnItems.map(({ item, flatIdx }) => (
              <ScoreCell
                key={item.id}
                item={item}
                flatIdx={flatIdx}
                value={scores[flatIdx] ?? null}
                taskPosition={taskPosition}
                idPrefix={idPrefix}
                registerInput={registerInput}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                onBlur={handleBlur}
                readOnly={readOnly}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Polite live region announces input-layer rejections (AC 6). */}
      <div aria-live="polite" className="sr-only">
        {liveMessage}
      </div>
    </div>
  );
}

interface ScoreCellProps {
  item: AdlItem;
  flatIdx: number;
  value: number | null;
  taskPosition: number;
  idPrefix: string;
  registerInput: (flatIdx: number, el: HTMLInputElement | null) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, flatIdx: number) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, flatIdx: number) => void;
  onBlur: () => void;
  readOnly: boolean;
}

/**
 * One score cell, memoized so a keystroke re-renders ONLY the edited cell — the grid's
 * callbacks are ref-stabilized, so the other 34 cells (unchanged `value`) bail out of
 * re-render (AC 12). The three visual states (AC 4): unscored = dashed border + `–`
 * placeholder; valid 1–4 = solid brand border + brand fill; out-of-range = error
 * border/fill (defense in depth — the input layer never commits a non-1–4 value).
 */
const ScoreCell = React.memo(function ScoreCell({
  item,
  flatIdx,
  value,
  taskPosition,
  idPrefix,
  registerInput,
  onKeyDown,
  onChange,
  onBlur,
  readOnly,
}: ScoreCellProps) {
  const display = value === null ? "" : String(value);
  const invalid = isInvalidScore(display);
  const unscored = display === "";
  const inputId = `${idPrefix}-cell-${flatIdx}`;
  return (
    <div
      role="gridcell"
      className="mb-2.5 grid grid-cols-[auto_minmax(0,1fr)_48px] items-center gap-x-2"
    >
      <span
        className="text-xs font-normal tabular-nums text-[color:var(--text-quaternary)]"
        aria-hidden="true"
      >
        {flatIdx + 1}.
      </span>
      <label
        htmlFor={inputId}
        className="break-words text-sm font-semibold leading-snug text-[color:var(--text-primary)]"
      >
        {item.description}
      </label>
      <input
        id={inputId}
        ref={(el) => registerInput(flatIdx, el)}
        type="text"
        inputMode="numeric"
        pattern="[1-4]"
        maxLength={1}
        value={display}
        placeholder="–"
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        aria-invalid={invalid || undefined}
        aria-label={`Task ${taskPosition} item ${flatIdx + 1} ${item.description}, score ${
          display || "not scored"
        }`}
        onKeyDown={readOnly ? undefined : (e) => onKeyDown(e, flatIdx)}
        onChange={readOnly ? undefined : (e) => onChange(e, flatIdx)}
        onBlur={readOnly ? undefined : onBlur}
        className={cn(
          "h-9 w-[48px] rounded-md border px-1.5 text-center text-md font-semibold tabular-nums transition-colors placeholder:font-normal placeholder:text-[color:var(--text-placeholder)]",
          readOnly
            ? // Read-only (Story 8.8): muted treatment, no focus ring — the value
              // reads as recorded data, not an affordance. Blanks stay dashed/blank.
              cn(
                "text-[color:var(--text-tertiary)]",
                unscored
                  ? "border-dashed border-[color:var(--border-secondary)] bg-[color:var(--bg-primary)]"
                  : "border-solid border-[color:var(--border-secondary)] bg-[color:var(--bg-secondary)]",
              )
            : cn(
                "text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)]",
                invalid
                  ? "border-[color:var(--border-error)] bg-[color:var(--bg-error-primary)] focus:ring-[color:var(--focus-ring-error)]"
                  : unscored
                    ? "border-dashed border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]"
                    : "border-solid border-[color:var(--border-brand-alt)] bg-[color:var(--bg-brand-primary)]",
              ),
        )}
      />
    </div>
  );
});

// =========================================================================== //
// Eval container — wires the draft store, the tabs, and per-task save-on-blur.
// =========================================================================== //

/** A fully-selected task (a non-empty `task_code`) — the only kind that gets a tab. */
interface SelectedScoringTask {
  task_code: string;
  task_category: string;
}

export function ScoringStep() {
  const store = useEvaluationDraft();
  const itemsQuery = useAdlItems();
  const ampsTasks = useAmpsTasks();
  const queryClient = useQueryClient();

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // Story 6.8 — the task + cell a Validation-Banner correction link asked us to focus
  // (null = none). Kept task-scoped so a later manual tab switch never steals focus.
  const [focusTarget, setFocusTarget] = React.useState<{ taskCode?: string; itemIndex: number } | null>(
    null,
  );
  // Clear the one-shot focus request once the grid has consumed it (stable identity so the
  // grid's focus effect doesn't re-run on unrelated re-renders).
  const clearFocusTarget = React.useCallback(() => setFocusTarget(null), []);

  // Mark the stepper position so the NEXT blur-save persists `current_step: "scoring"`
  // into draft_data (Story 6.7 resume then re-opens the exact step). Called
  // UNCONDITIONALLY on mount — even when `currentStep` was already "scoring" via
  // derivation, `draft.current_step` may be unset, so this syncs it. Post-render effect
  // (never dispatch during render); mount-once (the rail owns later step changes).
  const setCurrentStep = store.setCurrentStep;
  React.useEffect(() => {
    setCurrentStep("scoring");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only fully-selected tasks (non-empty code) get a tab (AC 1 / empty-state AC 10).
  const tasks = React.useMemo<SelectedScoringTask[]>(
    () =>
      (store.draft.tasks ?? [])
        .filter((t): t is SelectedScoringTask => Boolean(t?.task_code))
        .map((t) => ({ task_code: t.task_code, task_category: t.task_category })),
    [store.draft.tasks],
  );

  const items = React.useMemo(
    () => (Array.isArray(itemsQuery.data) ? clinicianAdlItems(itemsQuery.data) : []),
    [itemsQuery.data],
  );

  // Story 6.8 — consume a cross-step focus request: a Validation-Banner correction link
  // navigated here and queued the offending task + cell. Select that task's tab and hand
  // the cell index to the grid to focus, then clear the transient request so it fires once.
  const { pendingFocus, setPendingFocus } = store;
  React.useEffect(() => {
    if (!pendingFocus || pendingFocus.step !== "scoring") return;
    const idx = tasks.findIndex((t) => t.task_code === pendingFocus.taskCode);
    // Only arm focus when the task is actually selected — a stale/deselected taskCode would
    // otherwise strand `focusTarget` so it never matches the active task.
    if (idx >= 0) {
      setActiveIndex(idx);
      setFocusTarget({ taskCode: pendingFocus.taskCode, itemIndex: pendingFocus.itemIndex ?? 0 });
    }
    setPendingFocus(null);
    // tasks identity changes only when the selection changes — safe to depend on.
  }, [pendingFocus, setPendingFocus, tasks]);

  // Task name (tab short description) resolved by code from the AMPS catalogue (already
  // fetched by the tasks step; cached). Graceful fallback to the stored category.
  const nameByCode = React.useMemo(
    () =>
      new Map(
        (Array.isArray(ampsTasks.data) ? ampsTasks.data : []).map((t) => [t.code, t.name]),
      ),
    [ampsTasks.data],
  );

  // --- Single-flight save-on-blur (AC 7) ------------------------------------
  // The store is the immediate source of truth (keystroke + tab-switch latency never
  // wait on the network). A blur requests a save; saves are serialized (one in flight,
  // coalesce the latest) and each success re-anchors the version so the next
  // X-Client-Version is fresh. Refs hold the LATEST committed draft/version so the save
  // (run from an effect, AFTER the patch render) never sends a stale snapshot.
  const latestRef = React.useRef({ id: store.id, version: store.version, draft: store.draft });
  latestRef.current = { id: store.id, version: store.version, draft: store.draft };
  // The chain's version anchor is an IMPERATIVE ref, re-anchored synchronously in the ack
  // continuation — NEVER read back from render state. `store.setVersion` lands via a React
  // commit, and the `.finally → setSaveTick` effect can run on a commit that does not yet
  // include the `.then → setVersion` update (two separate microtasks — batching is not
  // guaranteed across them at production speed). Reading the version out of `latestRef` there
  // dispatched the next save with the PREVIOUS base version: a duplicate-base save-draft, a
  // server 409, and the yield-to-server reconcile below then discarded the freshly staged
  // scores (the deterministic results-flow W1 red of 2026-07-22 — captured on the wire as two
  // consecutive requests both carrying X-Client-Version: N). Render state only ever ADVANCES
  // this ref (a rehydrate/step-save elsewhere bumped it); the ack path owns it otherwise.
  const versionRef = React.useRef(store.version);
  if (store.version > versionRef.current) versionRef.current = store.version;
  const dirtyRef = React.useRef(false);
  const savingRef = React.useRef(false);
  const unmountedRef = React.useRef(false);
  const [saveTick, setSaveTick] = React.useState(0);

  // Track REAL unmount so an in-flight save's continuation can distinguish a genuine
  // teardown from a benign `saveTick` bump (a second-cell blur landing mid-flight).
  // Scoping the guard to unmount — NOT a per-effect `cancelled` closure that the save
  // effect's cleanup would flip on every tick — is what keeps overlapping saves
  // coalescing and re-anchoring the version. Otherwise a fast blur during network
  // latency cancels the in-flight save's `setVersion` + coalesce, the client is stranded
  // on a stale version, the next save 409s, and the reconcile discards the un-acked scores.
  React.useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  const requestSave = React.useCallback(() => {
    if (dirtyRef.current) setSaveTick((t) => t + 1);
  }, []);

  const handleScoreChange = React.useCallback(
    (taskCode: string, index: number, value: number | null) => {
      const current = scoresForTask(store.draft.scores, taskCode, items.length);
      const next = current.slice();
      next[index] = value;
      dirtyRef.current = true;
      setSaveError(null);
      store.patchDraft({ scores: { ...store.draft.scores, [taskCode]: next } });
    },
    [store, items.length],
  );

  React.useEffect(() => {
    if (saveTick === 0 || !dirtyRef.current || savingRef.current) return;
    savingRef.current = true;
    dirtyRef.current = false;
    const { id, draft } = latestRef.current;
    // The imperative anchor, not render state — see versionRef above.
    const version = versionRef.current;
    // The PROJECTED wire body — the shape the producer POSTs AND stores as `drafts.data`. Captured
    // once so the offline-cache reconcile below reuses the exact same body the producer persisted.
    const body = toEvaluationDraftBody(draft);
    // Story gh-458 — route save-on-blur through the offline write-path producer instead of the
    // online-only mutation. ONLINE: the producer POSTs directly and re-anchors the version, so the
    // 6.5 "saved" ack is unchanged; a 409 is re-thrown (only status 0 is treated as offline) and
    // still reconciles inline below. OFFLINE: it writes the local `drafts` row + collapses one
    // `update` op that drains on reconnect. Pass the PROJECTED body, not the raw store draft.
    saveEvaluationDraftNow({ evalId: id, version, data: body })
      .then((outcome) => {
        // A-02 / A-06a (19.6 review) — a refused save (foreign-owner draft, unreachable today via
        // owner-scoped reads) must not be treated as a success: nothing was written, so show the
        // save-error banner. Do NOT set dirtyRef here. The finally below treats a true dirtyRef as
        // "a change landed while this save was in flight" and immediately bumps saveTick, so arming
        // it on the refusal itself retries the same refused save forever. The next score change
        // sets dirtyRef in handleScoreChange, which is the retry the banner promises.
        if (outcome.refused) {
          if (!unmountedRef.current) setSaveError("Couldn't save your latest scores. They're kept here — they'll save on your next change.");
          return;
        }
        // The draft store and query cache are ROUTE-level and outlive this step: a save that
        // resolves after unmount (last cell blur → immediate "Continue to Review") must still
        // re-anchor the version and reconcile the cache, or the Review step replays the stale
        // version and its save/submit 409s with no client recovery. Only the component-local
        // banner state below stays unmount-guarded.
        // Offline → version unchanged (harmless no-op); online → re-anchored from the server ack.
        // The ref FIRST (synchronous — the next dispatch may run before the commit below lands).
        if (outcome.version > versionRef.current) versionRef.current = outcome.version;
        store.setVersion(outcome.version);
        // gh-459 (staleTime reconciliation) — keep the resume cache in lockstep with the local
        // write on BOTH paths. The full no-invalidate rationale (the blur-cadence refetch →
        // mid-session rehydrate → score-loss race, the results-flow W1 red of 2026-07-22) lives
        // on the shared helper — one cache contract with the setup/tasks step-save (PR #561).
        reconcileEvaluationDetailCache(queryClient, id, body, outcome);
        // A prior save may have failed; this one succeeded, so clear the stale banner.
        if (!unmountedRef.current) setSaveError(null);
      })
      .catch((error) => {
        // 409 EVAL-002: a genuine concurrent editor (second device) — reconcile from
        // the echoed server_state rather than surfacing a generic error (AC 9). Local
        // entries yield to the server's newer draft (optimistic-concurrency seam).
        const serverState = versionConflictServerState(error);
        if (isVersionConflict(error) && serverState) {
          if (serverState.version > versionRef.current) versionRef.current = serverState.version;
          store.setVersion(serverState.version);
          store.patchDraft(toEvaluationDraftBody(serverState.draft_data));
          dirtyRef.current = false;
          setSaveError(null);
          return;
        }
        // 422 EVAL-004 is unreachable given the 1–4 input rejection (AC 3); surface a
        // corrective inline message as defense in depth, never dropping local entries.
        if (error instanceof ApiError && error.code === EVAL_ERROR.SCORE_OUT_OF_RANGE) {
          setSaveError("A score was out of range and wasn't saved. Please re-check your entries.");
          return;
        }
        setSaveError("Couldn't save your latest scores. They're kept here — they'll save on your next change.");
      })
      .finally(() => {
        savingRef.current = false;
        // Coalesced follow-up: a change arrived while this save was in flight.
        if (!unmountedRef.current && dirtyRef.current) setSaveTick((t) => t + 1);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTick]);

  // --- Item fetch gates (AC 11) ---------------------------------------------
  if (itemsQuery.isPending) {
    return <ScoringSkeleton />;
  }
  if (itemsQuery.isError || items.length === 0) {
    return (
      <section className="grid gap-4">
        <Banner variant="danger" title="Couldn't load the ADL skill items">
          <p>The scoring grid needs the AMPS item list. Check your connection and try again.</p>
          <div className="mt-3">
            <Button type="button" variant="primary" size="sm" onClick={() => itemsQuery.refetch()}>
              Retry
            </Button>
          </div>
        </Banner>
      </section>
    );
  }

  // --- Empty-state guard (AC 10) --------------------------------------------
  if (tasks.length === 0) {
    return (
      <section className="grid gap-4">
        <header>
          <h1 className="text-lg font-semibold text-[color:var(--text-primary)]">Task scoring</h1>
        </header>
        <Banner variant="warn" title="Select your tasks before scoring">
          <p>
            Choose at least 2 AMPS tasks on the Tasks step, then return here to score the 35 ADL
            skill items for each one.
          </p>
          <div className="mt-3">
            <Button type="button" variant="primary" size="sm" onClick={() => store.setCurrentStep("tasks")}>
              Go to Tasks
            </Button>
          </div>
        </Banner>
      </section>
    );
  }

  const safeIndex = Math.min(activeIndex, tasks.length - 1);
  const activeTask = tasks[safeIndex];
  const activeScores = scoresForTask(store.draft.scores, activeTask.task_code, items.length);
  const panelId = "scoring-grid-panel";

  // Story 6.6 — 34/35 advisory state: the per-task banner (below) + the gated Continue CTA
  // (footer). The topbar aggregate pill is built in ResumeEvaluationRoute from this SAME
  // helper, so the pill and the gate never disagree. `missingCount` for the active task
  // drives the success/warn banner; `allTasksValid` gates forward navigation (AC 12–14).
  const completeness = computeScoringCompleteness(
    tasks,
    store.draft.scores,
    items.length,
  );
  const activeMissing = completeness.perTask[safeIndex]?.missingCount ?? items.length;
  const activeBanner = activeTaskBanner(safeIndex + 1, activeMissing);

  function switchTab(direction: 1 | -1) {
    setActiveIndex((i) => {
      const base = Math.min(i, tasks.length - 1);
      return (base + direction + tasks.length) % tasks.length;
    });
  }

  return (
    <section className="grid gap-[24px]">
      <header>
        <h1 className="text-lg font-semibold text-[color:var(--text-primary)]">Task scoring</h1>
        <p className="mt-1 text-sm text-[color:var(--text-tertiary)]">
          Each task is scored separately. Switch tabs to enter the 1–4 ordinal scores for the{" "}
          {items.length} ADL skill items. An empty cell (shown as “–”) is unscored.
        </p>
      </header>

      <div className="overflow-hidden rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] shadow-elevated">
        {/* Task tabs (AC 1) */}
        <div
          role="tablist"
          aria-label="Select a task to score"
          className="flex flex-wrap gap-2.5 border-b border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)] p-4"
        >
          {tasks.map((task, index) => {
            const isActive = index === safeIndex;
            const label = nameByCode.get(task.task_code) ?? task.task_category;
            return (
              <button
                key={task.task_code}
                role="tab"
                type="button"
                id={`scoring-tab-${index}`}
                aria-selected={isActive}
                aria-controls={panelId}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveIndex(index)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                    e.preventDefault();
                    switchTab(e.key === "ArrowRight" ? 1 : -1);
                  }
                }}
                className={cn(
                  "grid min-w-[220px] gap-1 rounded-md border px-3.5 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
                  isActive
                    ? "border-[color:var(--border-brand-alt)] bg-[color:var(--bg-brand-primary)]"
                    : "border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] hover:bg-[color:var(--bg-tertiary)]",
                )}
              >
                <span className="flex items-center gap-2">
                  <strong className="text-sm font-semibold text-[color:var(--text-primary)]">
                    Task {index + 1}
                  </strong>
                  <span className="text-xs font-semibold tabular-nums text-[color:var(--text-tertiary)]">
                    {task.task_code}
                  </span>
                </span>
                <span className="truncate text-sm text-[color:var(--text-tertiary)]">{label}</span>
              </button>
            );
          })}
        </div>

        {/* Active task's score grid (AC 2 — switching reads a different per-task array; the
            store retains every task's in-progress entries). */}
        <div
          role="tabpanel"
          id={panelId}
          aria-labelledby={`scoring-tab-${safeIndex}`}
          className="p-5"
        >
          <p className="mb-4 text-sm text-[color:var(--text-secondary)]">
            Ordinal scores (1–4) for{" "}
            <strong className="font-semibold text-[color:var(--text-primary)]">
              Task {safeIndex + 1}
            </strong>
            . Motor items 1–{MOTOR_ITEM_COUNT}, process items {MOTOR_ITEM_COUNT + 1}–{items.length}.
          </p>

          <ScoreEntryGrid
            key={activeTask.task_code}
            items={items}
            scores={activeScores}
            onScoreChange={(index, value) =>
              handleScoreChange(activeTask.task_code, index, value)
            }
            onCellBlur={requestSave}
            onSwitchTab={tasks.length > 1 ? switchTab : undefined}
            taskPosition={safeIndex + 1}
            gridLabel={`Task ${safeIndex + 1} score entry grid, ${items.length} items`}
            focusItemIndex={
              focusTarget && focusTarget.taskCode === activeTask.task_code
                ? focusTarget.itemIndex
                : null
            }
            onFocusHandled={clearFocusTarget}
          />
        </div>
      </div>

      {/* Per-task advisory (Story 6.6, AC 13/15). Corrective tone — banner.warn (muted
          amber), NEVER red. role="status" + aria-live polite so the active task's
          completeness change is announced without an assertive interrupt (the warn
          variant's default role="alert" is overridden here per AC 15). */}
      <Banner
        variant={activeBanner.variant}
        role="status"
        aria-live="polite"
        title={activeBanner.title}
      >
        {activeBanner.body}
      </Banner>

      {saveError ? (
        <p role="alert" className="text-sm text-[color:var(--text-error)]">
          {saveError}
        </p>
      ) : null}

      {/* Story 6.6 — forward nav is GATED: "Continue to Review" stays non-interactive
          (aria-disabled + pointer-events-none, mirroring Screen 06) until EVERY selected
          task satisfies the 34/35 rule. "Back to Tasks" stays ungated. The onClick is
          guarded too, so keyboard activation while blocked is a no-op (AC 14). */}
      <div className="flex flex-wrap justify-between gap-2.5">
        <Button type="button" variant="secondary" size="sm" onClick={() => store.setCurrentStep("tasks")}>
          Back to Tasks
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          aria-disabled={!completeness.allTasksValid}
          title={
            completeness.allTasksValid
              ? CONTINUE_TOOLTIP_ENABLED
              : CONTINUE_TOOLTIP_BLOCKED
          }
          className={cn(
            !completeness.allTasksValid && "pointer-events-none opacity-45",
          )}
          onClick={() => {
            if (completeness.allTasksValid) store.setCurrentStep("review");
          }}
        >
          Continue to Review
        </Button>
      </div>
    </section>
  );
}

/** Loading skeleton while the ADL item dictionary resolves (it gates the grid). */
function ScoringSkeleton() {
  return (
    <section aria-busy="true" role="status" className="grid gap-[24px]">
      <header>
        <h1 className="text-lg font-semibold text-[color:var(--text-primary)]">Task scoring</h1>
        <p className="mt-1 text-sm text-[color:var(--text-tertiary)]">Loading the AMPS skill items…</p>
      </header>
      <div className="h-[420px] animate-pulse rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)]" />
    </section>
  );
}

export default ScoringStep;
